import { reviews } from '@c/becomeASeller/SellerReviews';
import MobileDrawer from '@c/drawers/MobileDrawer';
import { useQuery } from '@tanstack/react-query';
import { getProductsByIds } from '@util/firestore/products';
import { formatCurrency } from '@util/index';
import { ChevronLeftIcon, ChevronRightIcon, StarIcon } from '@c/icons';
import { ReviewDocument } from 'models/review';
import Image from 'next/image';
import { SellerAccolade } from '@c/seller/Accolades';

interface MobileReviewModalProps {
  review: ReviewDocument | null;
  open: boolean;
  dismiss: () => void;
  onPrev?: () => void;
  onNext?: () => void;
}

export default function MobileReviewModal({
  review,
  open,
  dismiss,
  onPrev,
  onNext,
}: MobileReviewModalProps) {
  if (!review) return null;
  const validProducts = review.products.filter((p) => p.id);
  const { data: products } = useQuery({
    queryKey: ['products'],
    queryFn: () => getProductsByIds(validProducts.map((p) => p.id)),
    enabled: !!validProducts.length,
  });

  return (
    <MobileDrawer
      show={open}
      dismiss={dismiss}
      title="Purchased Item"
      maxHeight={90}
      topRounded
    >
      <div className="flex flex-col gap-y-4">
        <div className="relative h-[45rem] w-full">
          {onPrev && (
            <button
              type="button"
              aria-label="Previous review"
              onClick={onPrev}
              className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/80 p-2"
            >
              <ChevronLeftIcon className="h-6 w-6 text-brand-secondary" />
            </button>
          )}
          <Image
            src={review.images?.[0].thumb ?? ''}
            alt={review.products[0].title}
            className="h-full w-full"
            height={800}
            width={500}
          />
          {onNext && (
            <button
              type="button"
              aria-label="Next review"
              onClick={onNext}
              className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/80 p-2"
            >
              <ChevronRightIcon className="h-6 w-6 text-brand-secondary" />
            </button>
          )}

          {products && products[0] && (
            <div className="absolute bottom-8 flex w-full justify-center px-8">
              <div className="flex h-[10rem] w-full items-center rounded-[2rem] bg-white">
                <Image
                  src={products[0].thumbnail}
                  alt={products[0].title}
                  height={50}
                  width={50}
                  className="h-full w-[10rem] rounded-bl-[2rem]"
                />

                <div className="flex flex-col items-start justify-between pl-6">
                  <h3 className="w-[40rem] truncate text-[1.6rem] font-semibold text-brand-secondary">
                    {products[0].title}
                  </h3>

                  <span className="text-[1.8rem] font-bold text-black">
                    {formatCurrency(products[0].price)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-y-8 bg-white px-8 py-4">
          <div className="flex items-center gap-x-6">
            <Image
              src={review.reviewer_photo}
              alt={review.reviewer_username}
              height={50}
              width={50}
              className="h-full w-[6rem] rounded-full"
            />

            <span className="text-[2.1rem] font-semibold text-black underline">
              {review.reviewer_username}
            </span>

            {!!review.created && (
              <span className="text-[2.1rem] font-medium text-brand-dark-gray">
                {new Date(review.created ?? 0).toLocaleString('default', {
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
            )}
          </div>

          <div className="flex items-center gap-x-2">
            {[...Array(5)].map((_, i) => (
              <StarIcon
                key={`star-${i}`}
                className={`h-[4rem] w-[4rem] ${
                  i < review.rating
                    ? 'fill-brand-secondary text-brand-secondary'
                    : 'fill-zinc-300 text-zinc-300'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-8 overflow-x-scroll">
            {review.fast_shipper && (
              <SellerAccolade accolade="fast_shipper" count={1} />
            )}
            {review.as_described && (
              <SellerAccolade accolade="accurate_describer" count={1} />
            )}
            {review.quick_replies && (
              <SellerAccolade accolade="quick_responder" count={1} />
            )}
          </div>

          <p className="text-[2.1rem] font-medium text-zinc-800">
            {review.comment}
          </p>
        </div>
      </div>
    </MobileDrawer>
  );
}
